import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AppSidebar } from '@easyllama/ui-components';
import {
  LlamaLogo,
  LlamaSidebarCollapse,
  LlamaSidebarExpand,
  LlamaWordmark,
} from '@easyllama/ui-components/icons';
import '@easyllama/ui-components/styles.css';
import '@easyllama/ui-components/tokens.css';
import '~/organisms/AppNavigation/AppNavigationSidebar.css';
import { MOBILE_BREAKPOINT_PX } from './constants';
import { SIDEBAR_TOGGLE_ICON_CLASS, mapIconKey, resolveUiDashboardIcon } from './resolveIcon';
import {
  SwitchAccountInitialsAvatar,
  isCompanyContextFooterRow,
  CompanyFooterLeadingIcon,
} from './FooterItems';
import {
  isPathActive,
  findSelectedItemIdForPath,
} from './pathMatching';
import {
  persistSidebarCollapsedCookie,
  readSidebarCollapsedCookie,
  shouldForceSidebarCollapsed,
  SIDEBAR_EXPAND_EVENT,
} from './sidebarCollapse';
import {
  shouldUseRemoteJsNavigation,
  isGlobalSearchNavItem,
  submitRailsDeleteNavigation,
} from './railsNavigation';
import { installNavPreviewPortalMarkers } from './navPreviewPortalMarkers';
import { getContrastingColor } from '~/utils/getContrastingColor';
import GlobalSearchDialog from './GlobalSearch/GlobalSearchDialog';

// The announcement bar is position: fixed and publishes its measured height into
// --cta-banner-offset, resetting it to 0px when dismissed. Reading the variable rather than a
// prop keeps the sidebar below the bar and short by its height (without which its lower items
// are clipped) with no state shared between the two and no re-render either way.
export const BANNER_AWARE_SHELL_STYLE = {
  top: 'var(--cta-banner-offset, 0px)',
  height: 'calc(100vh - var(--cta-banner-offset, 0px))',
};

function getCurrentPathname() {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
}

export const TRAINING_NAV_LABEL = 'Training';
export const LIBRARY_NAV_LABEL = 'Library';
export const NEW_SIDEBAR_BADGE = { text: 'NEW', tone: 'purple' };

function toSidebarBadge(item) {
  const sourceBadge = item?.badge;
  if (!sourceBadge) return undefined;

  if (typeof sourceBadge === 'string') {
    return { text: sourceBadge, tone: 'green' };
  }

  if (typeof sourceBadge === 'object' && typeof sourceBadge.text === 'string') {
    return {
      text: sourceBadge.text,
      tone: sourceBadge.tone || 'green',
    };
  }

  return undefined;
}

function isNewSidebarBadge(badge) {
  return typeof badge?.text === 'string' && badge.text.toUpperCase() === 'NEW';
}

export function relocateTrainingLibraryNewBadge(item, isTrainingExpanded) {
  if (item?.label !== TRAINING_NAV_LABEL || !item.children?.length) return item;

  const libraryHasNew = item.children.some(
    (child) => child.label === LIBRARY_NAV_LABEL && isNewSidebarBadge(child.badge)
  );
  if (!libraryHasNew && !isNewSidebarBadge(item.badge)) return item;

  return {
    ...item,
    badge: isTrainingExpanded ? undefined : { ...NEW_SIDEBAR_BADGE },
    children: item.children.map((child) => {
      if (child.label !== LIBRARY_NAV_LABEL) return child;
      return { ...child, badge: { ...NEW_SIDEBAR_BADGE } };
    }),
  };
}

function isTrainingAccordionToggle(el) {
  if (!(el instanceof Element)) return false;
  if (el.getAttribute('aria-label') === TRAINING_NAV_LABEL) return true;

  // Desktop parent rows often omit aria-label; label + badge share the row text
  // (e.g. "TrainingNEW" or "Training NEW").
  const compact = (el.textContent || '').replace(/\s+/g, '');
  return (
    compact === TRAINING_NAV_LABEL
    || compact.startsWith(`${TRAINING_NAV_LABEL}NEW`)
    || compact.startsWith(`${TRAINING_NAV_LABEL}new`)
  );
}

export function readTrainingAccordionExpanded(root) {
  if (!root) return undefined;
  const toggles = root.querySelectorAll('a[aria-expanded], button[aria-expanded]');
  for (const el of toggles) {
    if (!isTrainingAccordionToggle(el)) continue;
    return el.getAttribute('aria-expanded') === 'true';
  }
  return undefined;
}

function toSidebarItem(item, idPrefix, pathname, navigationVariant) {
  const itemId = item?.id ? String(item.id) : idPrefix;
  const children = (item.children || []).map((child, idx) =>
    toSidebarItem(child, `${itemId}-${idx}`, pathname, navigationVariant)
  );

  // Check if any descendant (child, grandchild, etc.) is active
  const hasActiveDescendant = (function checkActive(node) {
    if (node.active) return true;
    return node.children?.some(child => checkActive(child)) ?? false;
  })({ children });

  // Item itself is active if its URL matches OR any descendant is active
  const isItemItselfActive = !!item.url && isPathActive(pathname, item.url);
  const isActive = isItemItselfActive || hasActiveDescendant;

  // Parent expanded if any child is active/expanded OR has active descendants
  const isExpanded = children.some((child) => child.active || child.expanded) || hasActiveDescendant;

  return {
    id: itemId,
    label: item.title || 'Untitled',
    badge: toSidebarBadge(item),
    notification: item?.notifications > 0 ? item?.notifications : undefined,
    path: item.url || undefined,
    iconKey: mapIconKey(item, navigationVariant),
    active: isActive,
    openInNewTab: item?.openInNewTab,
    asAction: item?.asAction,
    expanded: isExpanded,
    children: children.length ? children : undefined,
  };
}

function mapFooterRowsToItems(rows, pathname, navigationVariant) {
  return rows
    .map((row, idx) => {
      if (row.kind === 'switch_account') {
        const children = (row.accounts || []).map((account, accountIdx) => {
          const label = account.title || 'Account';
          return {
            id: `footer-switch-${idx}-${accountIdx}`,
            label,
            path: account.url || undefined,
            active: !!account.current || isPathActive(pathname, account.url),
            icon: <SwitchAccountInitialsAvatar label={label} />,
          };
        });

        return {
          id: `footer-switch-${idx}`,
          label: row.title || 'Switch account',
          iconKey: 'switch-account',
          expanded: children.some((child) => child.active),
          children,
        };
      }

      if (isCompanyContextFooterRow(row)) {
        const label = row.title || 'Item';
        return {
          id: `footer-${row.kind || 'item'}-${idx}`,
          label,
          path: row.url || undefined,
          icon: <CompanyFooterLeadingIcon row={row} />,
          selectable: false,
          active: false,
          openInNewTab: row?.openInNewTab,
        };
      }

      return {
        id: `footer-${row.kind || 'item'}-${idx}`,
        label: row.title || 'Item',
        path: row.url || undefined,
        iconKey: mapIconKey(row, navigationVariant),
        active: !!row.url && isPathActive(pathname, row.url),
        method: row.type ? String(row.type).toUpperCase() : 'GET',
        openInNewTab: row?.openInNewTab,
        asAction: row?.asAction,
      };
    })
    .filter(Boolean);
}

/** Must stay in sync with `AppSidebar` widths in `@easyllama/ui-components`. */
const SIDEBAR_EXPANDED_WIDTH_PX = 256;
const SIDEBAR_COLLAPSED_WIDTH_PX = 56;
// Shared with the Rails shell to offset page content and server-rendered filter panels.
const SIDEBAR_OFFSET_CSS_VAR = '--app-navigation-offset';
const FILTER_OFFSET_CSS_VAR = '--app-navigation-filter-offset';
const RAILS_FILTER_PANEL_SELECTOR = '.main-Sidebar.app-navigation-filters-active .menu-sidebar-accounts.menu-sidebar-accounts-library';
function getVisibleRailsFiltersPanel() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  const filterPanels = Array.from(document.querySelectorAll(RAILS_FILTER_PANEL_SELECTOR));
  return filterPanels.find((panel) => {
    const styles = window.getComputedStyle(panel);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;
    const rect = panel.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }) || null;
}

function getVisibleRailsFiltersOffsetPx() {
  const panel = getVisibleRailsFiltersPanel();
  if (!panel) return 0;
  return Math.round(panel.getBoundingClientRect().width);
}

const AppNavigationSidebar = (props) => {
  const flatSidebarLinks = useMemo(
    () => ({
      brandName: props.brandName,
      brandColor: props.brandColor,
      homeUrl: props.homeUrl,
      logoSrc: props.logoSrc,
      navigationVariant: props.navigationVariant,
      primaryNavigation: props.primaryNavigation,
      utilityNavigation: props.utilityNavigation,
      footerNavigation: props.footerNavigation,
      initialCollapsed: props.initialCollapsed,
      initialPathname: props.initialPathname,
      globalSearchRevampEnabled: props.globalSearchRevampEnabled,
      globalSearchUrl: props.globalSearchUrl,
    }),
    [
      props.brandName,
      props.brandColor,
      props.homeUrl,
      props.logoSrc,
      props.navigationVariant,
      props.primaryNavigation,
      props.utilityNavigation,
      props.footerNavigation,
      props.initialCollapsed,
      props.initialPathname,
      props.globalSearchRevampEnabled,
      props.globalSearchUrl,
    ]
  );
  const sidebarLinks = props.sidebar_links ?? flatSidebarLinks;
  const navigationVariant = String(sidebarLinks?.navigationVariant || 'default').toLowerCase();
  const initialPathname = sidebarLinks.initialPathname || getCurrentPathname();
  const initialCollapsed = Boolean(sidebarLinks.initialCollapsed);

  const [pathname, setPathname] = useState(initialPathname);
  // undefined = derive from route-active Training descendants; boolean = live accordion state
  const [trainingAccordionExpanded, setTrainingAccordionExpanded] = useState(undefined);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    shouldForceSidebarCollapsed(initialPathname) || initialCollapsed
  );
  const wasForcedCollapsedRef = useRef(shouldForceSidebarCollapsed(initialPathname));
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX
  );
  const [hasMounted, setHasMounted] = useState(false);
  // AppSidebar owns the mobile drawer itself (`mobileOpen` is left uncontrolled); `collapsed`
  // only tracks the desktop rail, so it cannot stand in for this.
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const sidebarShellRef = useRef(null);
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncPathname = () => setPathname(getCurrentPathname());
    window.addEventListener('popstate', syncPathname);
    window.addEventListener('turbo:load', syncPathname);
    window.addEventListener('turbo:render', syncPathname);

    return () => {
      window.removeEventListener('popstate', syncPathname);
      window.removeEventListener('turbo:load', syncPathname);
      window.removeEventListener('turbo:render', syncPathname);
    };
  }, []);

  useEffect(() => {
    if (!sidebarLinks?.globalSearchRevampEnabled) return undefined;

    const openGlobalSearchViaShortcut = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      if (isGlobalSearchOpen) {
        event.preventDefault();
        setIsGlobalSearchOpen(false);
        return;
      }
      const focusedTag = document.activeElement?.tagName;
      const isEditingText =
        focusedTag === 'INPUT' || focusedTag === 'TEXTAREA' || document.activeElement?.isContentEditable;
      if (isEditingText) return;

      event.preventDefault();
      setIsMobileNavOpen(false);
      setIsGlobalSearchOpen(true);
    };

    window.addEventListener('keydown', openGlobalSearchViaShortcut);
    return () => window.removeEventListener('keydown', openGlobalSearchViaShortcut);
  }, [sidebarLinks?.globalSearchRevampEnabled, isGlobalSearchOpen]);

  // Prefer route-derived expand after navigation; live accordion overrides until the next path change.
  useEffect(() => {
    setTrainingAccordionExpanded(undefined);
  }, [pathname]);

  // AppSidebar keeps accordion state internally (no onExpandChange). Watch aria-expanded so the
  // NEW badge can move between Training and Library when the section is toggled.
  useEffect(() => {
    const root = sidebarShellRef.current;
    if (!root || typeof MutationObserver === 'undefined') return undefined;

    const syncTrainingExpanded = () => {
      const expanded = readTrainingAccordionExpanded(root);
      if (expanded === undefined) return;
      setTrainingAccordionExpanded((current) => (current === expanded ? current : expanded));
    };

    const frame = window.requestAnimationFrame(syncTrainingExpanded);

    const observer = new MutationObserver(syncTrainingExpanded);
    observer.observe(root, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-expanded'],
      childList: true,
    });

    // React updates aria-expanded on re-render; click + rAF covers the toggle before the observer.
    const onClick = (event) => {
      const toggle = event.target instanceof Element
        ? event.target.closest('a[aria-expanded], button[aria-expanded]')
        : null;
      if (!toggle || !root.contains(toggle) || !isTrainingAccordionToggle(toggle)) return;
      window.requestAnimationFrame(syncTrainingExpanded);
    };
    root.addEventListener('click', onClick);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      root.removeEventListener('click', onClick);
    };
  }, []);

  // On navigation, force-collapse when entering the edit page; restore cookie preference when leaving
  useEffect(() => {
    const isForced = shouldForceSidebarCollapsed(pathname);
    if (isForced) {
      setIsSidebarCollapsed(true);
    } else if (wasForcedCollapsedRef.current) {
      setIsSidebarCollapsed(readSidebarCollapsedCookie());
    }
    wasForcedCollapsedRef.current = isForced;
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const expand = () => {
      if (shouldForceSidebarCollapsed(pathname)) return;
      setIsSidebarCollapsed(false);
    };
    window.addEventListener(SIDEBAR_EXPAND_EVENT, expand);
    return () => window.removeEventListener(SIDEBAR_EXPAND_EVENT, expand);
  }, [pathname]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    return installNavPreviewPortalMarkers();
  }, []);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return undefined;
    // Observe Rails filter panels when present; otherwise observe the React sidebar.
    const sidebarContainer = document.querySelector('.main-Sidebar') || document.querySelector('.app-navigation-preview');
    if (!sidebarContainer) return undefined;

    const applyOffsets = () => {
      const marginPx = isMobileViewport
        ? 0
        : (isSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH_PX : SIDEBAR_EXPANDED_WIDTH_PX);
      const filterOffsetPx = getVisibleRailsFiltersOffsetPx();
      document.documentElement.style.setProperty(SIDEBAR_OFFSET_CSS_VAR, `${marginPx}px`);
      document.documentElement.style.setProperty(FILTER_OFFSET_CSS_VAR, `${filterOffsetPx}px`);
    };

    applyOffsets();

    const observer = new MutationObserver(() => {
      applyOffsets();
    });

    observer.observe(sidebarContainer, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true,
      subtree: true,
    });

    const handleTransitionEnd = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.matches(RAILS_FILTER_PANEL_SELECTOR)) applyOffsets();
    };
    window.addEventListener('transitionend', handleTransitionEnd, true);

    return () => {
      observer.disconnect();
      window.removeEventListener('transitionend', handleTransitionEnd, true);
      document.documentElement.style.removeProperty(SIDEBAR_OFFSET_CSS_VAR);
      document.documentElement.style.removeProperty(FILTER_OFFSET_CSS_VAR);
    };
  }, [isMobileViewport, isSidebarCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncMobileViewport = () => {
      setIsMobileViewport(window.innerWidth < MOBILE_BREAKPOINT_PX);
    };

    syncMobileViewport();
    window.addEventListener('resize', syncMobileViewport);

    return () => window.removeEventListener('resize', syncMobileViewport);
  }, []);

  const data = useMemo(() => {
    const primaryNavigation = sidebarLinks?.primaryNavigation || [];
    const utilityNavigation = sidebarLinks?.utilityNavigation || [];
    const footerNavigation = sidebarLinks?.footerNavigation || [];

    const primaryItems = primaryNavigation.map((item, idx) =>
      toSidebarItem(item, `primary-${idx}`, pathname, navigationVariant)
    );
    const trainingItem = primaryItems.find((item) => item.label === TRAINING_NAV_LABEL);
    const isTrainingExpanded = !isSidebarCollapsed
      && (trainingAccordionExpanded ?? trainingItem?.expanded ?? false);
    const remappedPrimaryItems = primaryItems.map((item) =>
      relocateTrainingLibraryNewBadge(item, isTrainingExpanded)
    );

    const primarySections = [
      {
        id: 'primary',
        items: remappedPrimaryItems,
      },
      ...(utilityNavigation.length
        ? [{
          id: 'others',
          title: 'Others',
          items: utilityNavigation.map((item, idx) =>
            toSidebarItem(item, `utility-${idx}`, pathname, navigationVariant)
          ),
        }] : []
      ),
    ];
    const utilitySections = [
      {
        id: 'footer',
        items: mapFooterRowsToItems(footerNavigation, pathname, navigationVariant),
      },
    ];

    return {
      brandName: sidebarLinks?.brandName || 'EasyLlama',
      primarySections,
      utilitySections,
      defaultSelectedItemId: findSelectedItemIdForPath(
        [...primarySections, ...utilitySections],
        pathname
      ),
    };
  }, [pathname, sidebarLinks, trainingAccordionExpanded, isSidebarCollapsed]);

  const isForceCollapsed = shouldForceSidebarCollapsed(pathname);
  const companyLogoUrl = sidebarLinks?.logoSrc;
  const isLearnerVariant = navigationVariant === 'learners';
  const showCompanyLogo = isLearnerVariant && companyLogoUrl;
  const brandColor = isLearnerVariant ? sidebarLinks?.brandColor : undefined;

  const brandSidebarStyle = useMemo(() => {
    if (!brandColor) return undefined;
    const onAccent = getContrastingColor(brandColor);
    return {
      '--el-sidebar-item-active-bg': brandColor,
      '--el-sidebar-item-hover-bg': `color-mix(in srgb, ${brandColor} 20%, transparent)`,
      '--el-sidebar-item-active-icon': onAccent,
      '--el-sidebar-item-active-text': onAccent,
      '--el-sidebar-item-active-caret': onAccent,
    };
  }, [brandColor]);

  const renderCompanyLogoBranding = useMemo(() => {
    if (!showCompanyLogo) return undefined;
    return ({ collapsed, brandHref, brandName: name }) => {
      if (collapsed) {
        return (
          <a
            href={brandHref}
            aria-label={`${name} home`}
            className='flex items-center justify-center w-full h-10 rounded-[4px]'
          >
            <img
              src={companyLogoUrl}
              alt={name}
              style={{ maxWidth: 40, maxHeight: 40, width: 'auto', height: 'auto', objectFit: 'contain' }}
            />
          </a>
        );
      }
      return (
        <a
          href={brandHref}
          aria-label={`${name} home`}
          className='flex items-center justify-start w-full overflow-hidden rounded-[4px]'
        >
          <img
            src={companyLogoUrl}
            alt={name}
            style={{ maxWidth: 240, maxHeight: 64, width: 'auto', height: 'auto' }}
          />
        </a>
      );
    };
  }, [showCompanyLogo, companyLogoUrl]);

  return (
    <>
      <div
        ref={sidebarShellRef}
        className={`app-navigation-preview${hasMounted ? ' app-navigation-mounted' : ''}${isMobileNavOpen ? ' app-navigation-open' : ''}`}
        style={{
          position: 'fixed',
          left: 0,
          ...BANNER_AWARE_SHELL_STYLE,
          zIndex: isMobileViewport ? 1010 : 2,
          overflow: 'hidden',
          overscrollBehavior: 'contain',
          ...brandSidebarStyle,
        }}
      >
      <AppSidebar
        // AppSidebar's <aside> is el:h-screen, a flat viewport height that predates the bar,
        // so without this it outgrows the shortened shell and overflow:hidden crops the last
        // nav items. cn() is twMerge-backed and takes the consumer className last, so this
        // replaces the library height rather than fighting it. Mobile is left alone: there the
        // aside is fixed against the viewport and ignores the shell's box.
        className={isMobileViewport ? undefined : 'el:h-full el:max-h-full'}
        brandName={data.brandName}
        brandHref={sidebarLinks?.homeUrl || '/'}
        // EasyLlama logo/wordmark is the visible label; company name alone fails Label in Name.
        brandHomeAriaLabel={showCompanyLogo ? undefined : 'EasyLlama'}
        brandIcon={showCompanyLogo ? undefined : <LlamaLogo aria-hidden='true' className="el:w-full el:h-full el:shrink-0 el:text-(--el-sidebar-icon-color)" />}
        brandWordmark={showCompanyLogo ? undefined : <LlamaWordmark aria-hidden='true' className="el:w-[126.64px] el:h-6 el:shrink-0 el:text-(--el-sidebar-icon-color)" />}
        renderBranding={renderCompanyLogoBranding}
        primarySections={data.primarySections}
        utilitySections={data.utilitySections}
        defaultSelectedItemId={data.defaultSelectedItemId}
        renderEndContent={() => null}
        resolveIcon={resolveUiDashboardIcon}
        collapseToggleIcon={<LlamaSidebarCollapse className={SIDEBAR_TOGGLE_ICON_CLASS} />}
        expandToggleIcon={<LlamaSidebarExpand className={SIDEBAR_TOGGLE_ICON_CLASS} />}
        collapsed={isSidebarCollapsed}
        disableCollapseToggle={isForceCollapsed}
        onCollapsedChange={(nextCollapsed) => {
          if (!nextCollapsed && isForceCollapsed) return;
          setIsSidebarCollapsed(nextCollapsed);
          persistSidebarCollapsedCookie(nextCollapsed);
        }}
        onNavigate={({ path, sourceItem }) => {
          if (!path || path === '#') return;
          if (sidebarLinks?.globalSearchRevampEnabled && isGlobalSearchNavItem(path, sourceItem)) {
            setIsMobileNavOpen(false);
            setIsGlobalSearchOpen(true);
            return;
          }
          const method = String(sourceItem?.method || 'GET').toUpperCase();
          if (method === 'DELETE') {
            if (window.Rails?.ajax) {
              window.Rails.ajax({
                url: path,
                type: 'DELETE',
                dataType: 'script',
                success: () => {
                  window.location.reload();
                },
              });
              return;
            }

            submitRailsDeleteNavigation(path);
            return;
          }
          if (shouldUseRemoteJsNavigation(path, sourceItem) && window.Rails?.ajax) {
            window.Rails.ajax({ url: path, type: 'GET', dataType: 'script' });
            return;
          }
          if (sourceItem?.openInNewTab) {
            window.open(path, '_blank', 'noopener,noreferrer');
            return;
          }
          window.location.assign(path);
        }}
        mobileBreakpointPx={MOBILE_BREAKPOINT_PX}
        mobileOpen={isMobileNavOpen}
        onMobileOpenChange={setIsMobileNavOpen}
        mobileToggleClassName="app-navigation-mobile-toggle"
        mobileFullWidth
        overflowBlur="bottom"
      />
      </div>
      {sidebarLinks?.globalSearchRevampEnabled && (
        <GlobalSearchDialog
          open={isGlobalSearchOpen}
          onOpenChange={setIsGlobalSearchOpen}
          searchUrl={sidebarLinks?.globalSearchUrl}
        />
      )}
    </>
  );
};

AppNavigationSidebar.propTypes = {
  sidebar_links: PropTypes.shape({
    brandName: PropTypes.string,
    brandColor: PropTypes.string,
    navigationVariant: PropTypes.string,
    primaryNavigation: PropTypes.arrayOf(PropTypes.object),
    utilityNavigation: PropTypes.arrayOf(PropTypes.object),
    footerNavigation: PropTypes.arrayOf(PropTypes.object),
    initialCollapsed: PropTypes.bool,
    initialPathname: PropTypes.string,
    globalSearchRevampEnabled: PropTypes.bool,
    globalSearchUrl: PropTypes.string,
  }),
  brandName: PropTypes.string,
  brandColor: PropTypes.string,
  homeUrl: PropTypes.string,
  navigationVariant: PropTypes.string,
  primaryNavigation: PropTypes.arrayOf(PropTypes.object),
  utilityNavigation: PropTypes.arrayOf(PropTypes.object),
  footerNavigation: PropTypes.arrayOf(PropTypes.object),
  initialCollapsed: PropTypes.bool,
  initialPathname: PropTypes.string,
  globalSearchRevampEnabled: PropTypes.bool,
  globalSearchUrl: PropTypes.string,
};

export default AppNavigationSidebar;
